export type FlavorId = 'orbit';

export interface FlavorColors {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
  text: string;
  mutedText: string;
  border: string;
}

export type FlavorColorToken =
  | ''
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'dark'
  | 'medium'
  | 'light'
  | 'white'
  | 'secondary-gradient';

interface VirtualCardDesign {
  background: string;
  backgroundImageFile?: string;
  textColor: string;
  labelColor: string;
  barcodeBackground: string;
  borderColor: string;
  logoFile: string;
  logoShadow: string;
  logoTop: string;
  logoRight: string;
  logoWidth: string;
  logoHeight: string;
}

export interface FlavorUiConfig {
  toolbarColor: FlavorColorToken;
  toolbarImageSize: string;
  toolbarMinHeight: string;
  headerSeparatorColor: FlavorColorToken;
  cardNumberBackgroundColor: FlavorColorToken;
  cardActionColor: Exclude<FlavorColorToken, ''>;
  dashboardBackgroundColor: Exclude<FlavorColorToken, ''>;
  dashboardBackgroundImage: string;
  dashboardBalanceColor: FlavorColorToken;
  sideMenuColor: Exclude<FlavorColorToken, ''>;
  virtualCard: VirtualCardDesign;
}

export interface IonicColorScale {
  base: string;
  rgb: string;
  contrast: string;
  contrastRgb: string;
  shade: string;
  tint: string;
}

export interface FlavorIonicPalette {
  primary: IonicColorScale;
  secondary: IonicColorScale;
  tertiary: IonicColorScale;
}

export interface FlavorConfig {
  id: FlavorId;
  appName: string;
  appId: string;
  retailGroupId: number;
  defaultThemeId: string;
  supportNumber: string;
  logoPath: string;
  showFooter: boolean;
  colors: FlavorColors;
}

export interface FlavorStoreLinks {
  androidPackageId: string;
  iosAppId: string;
  appGalleryAppId: string;
}

const DEFAULT_FLAVOR_ID: FlavorId = 'orbit';

const FLAVOR_CONFIGS: Record<FlavorId, FlavorConfig> = {
  orbit: {
    id: 'orbit',
    appName: 'Orbit Loyalty',
    appId: 'za.co.one1.app.InfinityRewards',
    retailGroupId: 180,
    defaultThemeId: 'AppThemeOrbit',
    supportNumber: '0861663111',
    logoPath: 'assets/orbit-wordmark.png',
    showFooter: false,
    colors: {
      primary: '#294FA1',
      secondary: '#313234',
      tertiary: '#67CDE1',
      background: '#F7FBFC',
      text: '#313234',
      mutedText: 'rgba(91, 95, 96, 0.82)',
      border: 'rgba(49, 50, 52, 0.12)',
    },
  },
};

export const FLAVOR_STORE_LINKS: Record<FlavorId, FlavorStoreLinks> = {
  orbit: {
    androidPackageId: 'za.co.one1.app.InfinityRewards',
    iosAppId: '1415754074',
    appGalleryAppId: '109570849',
  },
};

export const LEGACY_FLAVOR_UI: Record<FlavorId, FlavorUiConfig> = {
  orbit: {
    toolbarColor: '',
    toolbarImageSize: '22px',
    toolbarMinHeight: '65px',
    headerSeparatorColor: '',
    cardNumberBackgroundColor: '',
    cardActionColor: 'primary',
    dashboardBackgroundColor: 'dark',
    dashboardBackgroundImage: '',
    dashboardBalanceColor: 'white',
    sideMenuColor: 'dark',
    virtualCard: {
      background: 'linear-gradient(135deg, #2b2e34 0%, #1f2126 100%)',
      backgroundImageFile: 'vc-orbit.png',
      textColor: '#ffffff',
      labelColor: 'rgba(255, 255, 255, 0.86)',
      barcodeBackground: 'rgba(255, 255, 255, 0.94)',
      borderColor: 'rgba(255, 255, 255, 0.24)',
      logoFile: 'logo-card.svg',
      logoShadow:
        'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 3px rgba(255, 255, 255, 0.2))',
      logoTop: '24px',
      logoRight: '16px',
      logoWidth: '30%',
      logoHeight: '40px',
    },
  },
};

export const LEGACY_IONIC_PALETTES: Record<FlavorId, FlavorIonicPalette> = {
  orbit: {
    primary: {
      base: '#294FA1',
      rgb: '41,79,161',
      contrast: '#ffffff',
      contrastRgb: '255,255,255',
      shade: '#24468E',
      tint: '#3E61AA',
    },
    secondary: {
      base: '#313234',
      rgb: '49,50,52',
      contrast: '#ffffff',
      contrastRgb: '255,255,255',
      shade: '#2B2C2E',
      tint: '#464749',
    },
    tertiary: {
      base: '#67CDE1',
      rgb: '103,205,225',
      contrast: '#040606',
      contrastRgb: '4,6,6',
      shade: '#5BB4C6',
      tint: '#76D2E4',
    },
  },
};

export function getFlavorConfig(_flavorId?: string): FlavorConfig {
  return FLAVOR_CONFIGS[DEFAULT_FLAVOR_ID];
}
